/**
 * Responsive columns.
 *
 * A columns block lays its cells (text blocks) side by side; any text block can
 * also flow its own text into columns. Both are set per device, the way theme
 * overrides are: `base` is desktop, and `tablet` and `phone` hold only what
 * differs. A device without its own value uses desktop's, except that a phone
 * stacks columns into one unless it is given a count.
 *
 *   block.layout = { base?, tablet?, phone? }  each { count, widths, gap, rowGap, order }
 *   block.flow   = { base?, tablet?, phone? }  each { count, gap }
 *
 * Pure, so the server render, the editor and tests share it.
 */

import { columnColors } from './colors.js';
import { cssSpace, parseGap, serializeGap } from './spacing.js';
import {
    COLUMNS_WIDTH_FALLBACK,
    COLUMNS_WIDTH_RANGES,
    TEXT_WIDTH_RANGES,
    clampContentWidth,
    contentWidthCss,
} from './content-width.js';

export const COLUMN_LAYERS = ['base', 'tablet', 'phone'];
export const MAX_CELLS = 4;
export const MAX_FLOW = 4;
export const MAX_WIDTH_RATIO = 12;

/** Width of the whole columns block; bare numbers are ch, like text. */
export const COLUMNS_WIDTH = {
    min: COLUMNS_WIDTH_RANGES.ch.min,
    max: COLUMNS_WIDTH_RANGES.ch.max,
    fallback: COLUMNS_WIDTH_FALLBACK,
};

/** Clamped stored width (number in ch, or a length string). */
export function columnsWidth(block) {
    return clampContentWidth(block?.maxWidth, COLUMNS_WIDTH_RANGES, COLUMNS_WIDTH.fallback);
}

/** CSS max-width for the columns frame. */
export function columnsWidthCss(block) {
    return contentWidthCss(columnsWidth(block), COLUMNS_WIDTH.fallback);
}

/** Media queries for each layer; the same ones the theme overrides use. */
export const COLUMN_MEDIA = {
    tablet: '(min-width: 601px) and (max-width: 1024px)',
    phone: '(max-width: 600px)',
};

const DEFAULTS = {
    layout: { gap: { value: 1.5, unit: 'rem' }, rowGap: { value: 2, unit: 'rem' } },
    flow: { count: 1, gap: { value: 2, unit: 'rem' } },
};

const PHONE_COUNT = 1;

const layerOf = (layer) => (COLUMN_LAYERS.includes(layer) ? layer : 'base');
const own = (block, key, layer) => block?.[key]?.[layerOf(layer)] || {};
const wholeNumber = (value, min, max, fallback) => {
    const n = Math.round(Number(value));
    return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : fallback;
};

/**
 * The stored value for one field on one device, falling back the way the
 * render does: the device's own, then the phone's stacking default, then desktop.
 * @returns {*} undefined when nothing is set anywhere
 */
export function columnValue(block, key, layer, field) {
    const at = layerOf(layer);
    const mine = own(block, key, at)[field];
    if (mine !== undefined) return mine;
    if (at === 'phone' && field === 'count') return PHONE_COUNT;
    return at === 'base' ? undefined : own(block, key, 'base')[field];
}

/** True when a tablet or phone value differs from desktop's on purpose. */
export function columnOverridden(block, key, layer, field) {
    return layerOf(layer) !== 'base' && own(block, key, layer)[field] !== undefined;
}

/**
 * The block's `key` map with one field set (or cleared with undefined) on one
 * device. Empty devices and an empty map drop out, so a block with nothing set
 * has no `layout` or `flow` at all.
 * @returns {Object|undefined}
 */
export function setColumnValue(block, key, layer, field, value) {
    const at = layerOf(layer);
    const map = { ...(block?.[key] || {}) };
    const entry = { ...(map[at] || {}) };
    if (value === undefined) delete entry[field];
    else entry[field] = value;
    if (Object.keys(entry).length) map[at] = entry;
    else delete map[at];
    return Object.keys(map).length ? map : undefined;
}

const gapCss = (raw, fallback) => {
    const parsed = parseGap(raw ?? fallback);
    return parsed.legacy ? `${fallback.value}${fallback.unit}` : `${parsed.value}${parsed.unit}`;
};

/** Stored form of a gap typed in the inspector. */
export const columnGap = (value, unit) => serializeGap(value, unit);

/** The cells' ids in the order a device shows them. Ids it does not list keep their place after the listed ones. */
export function cellOrder(block, layer) {
    const ids = (block?.cells || []).map((cell) => cell?.id).filter(Boolean);
    const listed = columnValue(block, 'layout', layer, 'order');
    if (!Array.isArray(listed)) return ids;
    const known = new Set(ids);
    const first = [...new Set(listed.filter((id) => known.has(id)))];
    const placed = new Set(first);
    return [...first, ...ids.filter((id) => !placed.has(id))];
}

/**
 * The layout a device renders.
 * @returns {{ count: number, widths: number[], gap: string, rowGap: string, order: string[] }}
 */
export function resolveColumns(block, layer) {
    const cells = Math.max(1, Math.min(MAX_CELLS, (block?.cells || []).length || 1));
    const count = wholeNumber(columnValue(block, 'layout', layer, 'count') ?? cells, 1, cells, cells);
    const stored = columnValue(block, 'layout', layer, 'widths');
    const widths = Array.isArray(stored) && stored.length === count
        && stored.every((value) => Number(value) > 0)
        ? stored.map((value) => Math.min(MAX_WIDTH_RATIO, Number(value)))
        : Array(count).fill(1);
    return {
        count,
        widths,
        gap: gapCss(columnValue(block, 'layout', layer, 'gap'), DEFAULTS.layout.gap),
        rowGap: gapCss(columnValue(block, 'layout', layer, 'rowGap'), DEFAULTS.layout.rowGap),
        order: cellOrder(block, layer),
    };
}

/** How a device flows a text block's text. */
export function resolveFlow(block, layer) {
    return {
        count: wholeNumber(columnValue(block, 'flow', layer, 'count') ?? DEFAULTS.flow.count, 1, MAX_FLOW, 1),
        gap: gapCss(columnValue(block, 'flow', layer, 'gap'), DEFAULTS.flow.gap),
    };
}

/** CSS variables for a columns block's grid: one set per device. */
export function columnsStyle(block) {
    const vars = {};
    COLUMN_LAYERS.forEach((layer) => {
        const { widths, gap, rowGap } = resolveColumns(block, layer);
        vars[`--cols-${layer}`] = widths.map((width) => `minmax(0, ${width}fr)`).join(' ');
        vars[`--col-gap-${layer}`] = gap;
        vars[`--row-gap-${layer}`] = rowGap;
    });
    // A coloured column fills the block's top and bottom spacing too.
    vars['--cols-space-top'] = cssSpace(block?.marginTop) || '0px';
    vars['--cols-space-bottom'] = cssSpace(block?.marginBottom) || '0px';
    return vars;
}

const WINDOW_EDGE = '100vmax';

function cellPlace(block, layer, cellId) {
    const { count, order } = resolveColumns(block, layer);
    const at = Math.max(0, order.indexOf(cellId));
    return {
        count,
        column: at % count,
        row: Math.floor(at / count),
        rows: Math.ceil(order.length / count),
    };
}

/**
 * How far a coloured cell's background reaches past the cell on one device,
 * as a clip-path for the fill: half the gap towards a neighbour, the block's
 * spacing at the top and bottom, and the window edge on an outer side.
 */
function fillClip(block, layer, cellId) {
    const { count, column, row, rows } = cellPlace(block, layer, cellId);
    const halfGap = `var(--col-gap-${layer}) / 2`;
    const halfRow = `var(--row-gap-${layer}) / 2`;
    const top = row === 0 ? 'var(--cols-space-top)' : halfRow;
    const bottom = row === rows - 1 ? 'var(--cols-space-bottom)' : halfRow;
    const left = column === 0 ? WINDOW_EDGE : halfGap;
    const right = column === count - 1 ? WINDOW_EDGE : halfGap;
    return `inset(${[top, right, bottom, left].map((side) => `calc(-1 * (${side}))`).join(' ')})`;
}

/**
 * CSS variables for one cell: its place on each device and, when it has its
 * own colours, those and how far its background reaches.
 * @param {Object} [theme] - Site theme, for saved palettes
 */
export function cellStyle(block, cellId, theme) {
    const vars = {};
    COLUMN_LAYERS.forEach((layer) => {
        vars[`--cell-order-${layer}`] = String(Math.max(0, cellOrder(block, layer).indexOf(cellId)));
    });
    const cell = (block?.cells || []).find((entry) => entry?.id === cellId);
    const colors = columnColors(cell?.colorTheme, theme);
    if (colors.fill) {
        COLUMN_LAYERS.forEach((layer) => {
            vars[`--fill-clip-${layer}`] = fillClip(block, layer, cellId);
            // A row that wraps keeps the block's spacing inside each coloured cell;
            // the outer rows already get it from the block's own margin.
            const { row, rows } = cellPlace(block, layer, cellId);
            vars[`--cell-pad-top-${layer}`] = row === 0 ? '0px' : 'var(--cols-space-top)';
            vars[`--cell-pad-bottom-${layer}`] = row === rows - 1 ? '0px' : 'var(--cols-space-bottom)';
        });
    }
    return { ...vars, ...colors.style };
}

/** True when a cell paints its own background. */
export function cellFilled(block, cellId, theme) {
    const cell = (block?.cells || []).find((entry) => entry?.id === cellId);
    return columnColors(cell?.colorTheme, theme).fill;
}

/** CSS variables for a text block that flows into columns, or null when it never does. */
export function flowStyle(block) {
    if (!block?.flow) return null;
    const resolved = COLUMN_LAYERS.map((layer) => [layer, resolveFlow(block, layer)]);
    if (resolved.every(([, flow]) => flow.count === 1)) return null;
    const vars = {};
    resolved.forEach(([layer, flow]) => {
        vars[`--flow-${layer}`] = String(flow.count);
        vars[`--flow-gap-${layer}`] = flow.gap;
    });
    return vars;
}

/** Column count change on one device, dropping widths that no longer fit. */
export function withColumnCount(block, layer, count) {
    let layout = setColumnValue(block, 'layout', layer, 'count', count);
    const widths = layout?.[layerOf(layer)]?.widths;
    if (Array.isArray(widths) && widths.length !== count) {
        layout = setColumnValue({ layout }, 'layout', layer, 'widths', undefined);
    }
    return layout;
}

/** Cells resized to `count`, new ones made by `makeCell`; layouts forget removed cells. */
export function withCellCount(block, count, makeCell) {
    const next = Math.max(2, Math.min(MAX_CELLS, Math.round(Number(count)) || 2));
    const cells = [...(block?.cells || [])];
    while (cells.length < next) cells.push(makeCell());
    cells.length = next;
    const kept = new Set(cells.map((cell) => cell.id));
    let layout = block?.layout;
    COLUMN_LAYERS.forEach((layer) => {
        const entry = layout?.[layer];
        if (!entry) return;
        let current = { layout };
        if (entry.count > next) current = { layout: setColumnValue(current, 'layout', layer, 'count', next) };
        if (Array.isArray(entry.widths) && entry.widths.length > next) {
            current = { layout: setColumnValue(current, 'layout', layer, 'widths', undefined) };
        }
        if (Array.isArray(entry.order)) {
            current = { layout: setColumnValue(current, 'layout', layer, 'order', entry.order.filter((id) => kept.has(id))) };
        }
        layout = current.layout;
    });
    return { cells, layout };
}

/**
 * A text block turned into columns: its text in the first, an empty one beside it.
 * The text keeps its id, unless it is a stand-in for page.content ("legacy-…"),
 * and its content width becomes the block's, so the page keeps its measure.
 */
export function splitIntoColumns(block, { makeColumns, makeCell }) {
    const { marginTop, marginBottom, filter, colorTheme, maxWidth, ...text } = block;
    const cell = String(text.id || '').startsWith('legacy-') ? { ...text, id: makeCell().id } : text;
    return {
        ...makeColumns(),
        ...(maxWidth !== undefined ? { maxWidth } : {}),
        ...(marginTop !== undefined ? { marginTop } : {}),
        ...(marginBottom !== undefined ? { marginBottom } : {}),
        ...(filter !== undefined ? { filter } : {}),
        ...(colorTheme !== undefined ? { colorTheme } : {}),
        cells: [cell, makeCell()],
    };
}

/** Columns back into one text block: the cells' text one after another, in desktop order. */
export function mergeColumns(block, { makeText }) {
    const byId = new Map((block.cells || []).map((cell) => [cell.id, cell]));
    const html = cellOrder(block, 'base')
        .map((id) => String(byId.get(id)?.html || '').trim())
        .filter(Boolean)
        .join('\n');
    const first = block.cells?.[0] || {};
    const { marginTop, marginBottom, filter, colorTheme } = block;
    const wide = block.maxWidth ?? first.maxWidth;
    const maxWidth = wide === undefined
        ? undefined
        : clampContentWidth(wide, TEXT_WIDTH_RANGES, wide);
    return {
        ...makeText(),
        ...(maxWidth !== undefined ? { maxWidth } : {}),
        ...(first.flow ? { flow: first.flow } : {}),
        html,
        ...(marginTop !== undefined ? { marginTop } : {}),
        ...(marginBottom !== undefined ? { marginBottom } : {}),
        ...(filter !== undefined ? { filter } : {}),
        ...(colorTheme !== undefined ? { colorTheme } : {}),
    };
}

/** "Desktop", "Tablet", "Phone" for inspector labels. */
export function layerName(layer) {
    return { base: 'Desktop', tablet: 'Tablet', phone: 'Phone' }[layerOf(layer)];
}

export default {
    COLUMN_LAYERS,
    COLUMN_MEDIA,
    COLUMNS_WIDTH,
    columnsWidth,
    columnsWidthCss,
    columnValue,
    columnOverridden,
    setColumnValue,
    resolveColumns,
    resolveFlow,
    columnsStyle,
    cellStyle,
    cellFilled,
    flowStyle,
    withColumnCount,
    withCellCount,
    splitIntoColumns,
    mergeColumns,
};
